import { useCallback } from 'react';
import { X, ChevronUp, ChevronDown, Plus } from 'lucide-react';

export const RECIPIENT_COLORS = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899',
];

const ROLES = [
  { value: 'signer', label: 'Signer' },
  { value: 'cc', label: 'CC' },
  { value: 'approver', label: 'Approver' },
];

function newRecipient() {
  // Client-assigned UUID so the id is stable across sync cycles.
  // Without this, every replaceRecipients call produces fresh server UUIDs
  // and any fields already referencing the recipient get orphaned.
  const id = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `local-${Math.random().toString(36).slice(2)}-${Date.now()}`;
  return { id, name: '', email: '', role: 'signer' };
}

export default function RecipientList({
  recipients,
  onChange,
  routing,
  onRoutingChange,
}) {
  const add = useCallback(() => {
    onChange([...recipients, newRecipient()]);
  }, [recipients, onChange]);

  const remove = useCallback(
    (idx) => {
      onChange(recipients.filter((_, i) => i !== idx));
    },
    [recipients, onChange],
  );

  const update = useCallback(
    (idx, key, value) => {
      onChange(recipients.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
    },
    [recipients, onChange],
  );

  const moveUp = useCallback(
    (idx) => {
      if (idx === 0) return;
      const next = [...recipients];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      onChange(next);
    },
    [recipients, onChange],
  );

  const moveDown = useCallback(
    (idx) => {
      if (idx >= recipients.length - 1) return;
      const next = [...recipients];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      onChange(next);
    },
    [recipients, onChange],
  );

  return (
    <div className="space-y-4">
      {/* Routing toggle */}
      <div className="flex items-center gap-4">
        <span className="text-sm font-medium text-gray-700">Signing order:</span>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            name="routing"
            value="parallel"
            checked={routing === 'parallel'}
            onChange={() => onRoutingChange('parallel')}
            className="text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-600">Everyone at once</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            name="routing"
            value="sequential"
            checked={routing === 'sequential'}
            onChange={() => onRoutingChange('sequential')}
            className="text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-600">In order</span>
        </label>
      </div>

      {/* Recipient rows */}
      <div className="space-y-3">
        {recipients.map((r, idx) => {
          const color = RECIPIENT_COLORS[idx % RECIPIENT_COLORS.length];
          return (
            <div
              key={idx}
              className="flex items-center gap-2 bg-gray-50 rounded-lg p-3 border border-gray-200"
            >
              {/* Sequential order number + arrows */}
              {routing === 'sequential' && (
                <div className="flex flex-col items-center gap-0.5 mr-1">
                  <button
                    type="button"
                    onClick={() => moveUp(idx)}
                    disabled={idx === 0}
                    className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  >
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-xs font-semibold text-gray-500 leading-none">
                    {idx + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => moveDown(idx)}
                    disabled={idx >= recipients.length - 1}
                    className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* Color dot */}
              <div
                className="w-3 h-3 rounded-full shrink-0"
                style={{ backgroundColor: color }}
              />

              {/* Name */}
              <input
                type="text"
                placeholder="Name"
                value={r.name}
                onChange={(e) => update(idx, 'name', e.target.value)}
                className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />

              {/* Email */}
              <input
                type="email"
                placeholder="Email"
                value={r.email}
                onChange={(e) => update(idx, 'email', e.target.value)}
                className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />

              {/* Role */}
              <select
                value={r.role}
                onChange={(e) => update(idx, 'role', e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {ROLES.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>

              {/* Remove */}
              <button
                type="button"
                onClick={() => remove(idx)}
                className="p-1 text-gray-400 hover:text-red-500 transition shrink-0"
                aria-label="Remove recipient"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Add button */}
      <button
        type="button"
        onClick={add}
        className="flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition"
      >
        <Plus className="w-4 h-4" />
        Add recipient
      </button>
    </div>
  );
}
